import type { GetStaticPropsContext, InferGetStaticPropsType } from "next";

import PageProvider from "../contexts/PageProvider";
import Layout from "../components/layout/LayoutWrapper";
import PageInfo from "../components/page/PageInfo";
import PageEditorial from "../components/page/PageEditorial";
import PageKnowledge from "../components/page/PageKnowledge";
import PageStockists from "../components/page/PageStockists";
import PageContact from "../components/page/PageContact";
import { NextSeo } from "next-seo";
import { formatSanitySeo } from "../utils/seo";
import { SHOPIFY_DEFAULT_LOCATION } from "library/utils/shopify";
import {
  fetchPage,
  fetchPagePaths,
  routeFetcher,
} from "library/services/fetchers";
import { staticPropsError } from "../utils/error";
import PageLoading from "../components/page/PageLoading";
import PageMissing from "../components/page/PageMissing";
import { createHashKey } from "library/utils";
import { isProduction } from "../hooks/useFlags";

export const getStaticPaths = fetchPagePaths;

export async function getStaticProps({
  params,
  preview = false,
  locale = SHOPIFY_DEFAULT_LOCATION,
}: GetStaticPropsContext<{
  handle: string;
}>) {
  try {
    const handle = params?.handle;
    const key = createHashKey("page", { locale, preview, handle });
    const data = await routeFetcher({
      key,
      fetcher: () => fetchPage({ locale, preview, handle }),
      preview,
    });

    return {
      revalidate: isProduction ? undefined : 1,
      props: { data },
    };
  } catch (e: any) {
    return staticPropsError(e);
  }
}

export default function Page({
  data,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  if (data?.error) return <PageLoading />;

  const page = data?.page;
  if (!page) return <PageMissing />;

  return (
    <>
      <NextSeo {...formatSanitySeo(`/${data.handle}`, page)} />

      <PageProvider page={page}>
        {page.layout == "info" && <PageInfo />}
        {page.layout == "editorial.sidebar" && <PageEditorial />}
        {page.layout == "editorial.landing" && <PageEditorial />}
        {page.layout == "editorial.standard" && <PageEditorial />}
        {page.layout == "knowledge" && <PageKnowledge />}
        {page.layout == "stockists" && <PageStockists />}
        {page.layout == "contact" && <PageContact />}
      </PageProvider>
    </>
  );
}

Page.Layout = Layout;
